import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User, AlertTriangle, Heart, Trophy,
    Settings, ChevronRight, ChevronLeft, LogOut, Camera, Edit2,
    Eye, EyeOff, Clock, Target, Zap, Lock, X,
    Shield, MapPin, Phone, MessageSquare, Share2
} from 'lucide-react';
import { Contact, SafetyStatus } from '../types';
import { contactService, getEffectiveAvatar } from '../services/contactService';

// --- BADGE DEFINITIONS ---
const ALL_BADGES = [
    { id: 'first_alert', name: 'First Alert', icon: '🆘', description: 'Report your first hazard', tier: 'bronze', requirement: 'Report 1 hazard' },
    { id: 'watchdog', name: 'Watchdog', icon: '📡', description: 'Report 5 hazards', tier: 'silver', requirement: 'Report 5 hazards' },
    { id: 'guardian', name: 'Guardian', icon: '🛡️', description: 'Report 10 hazards', tier: 'gold', requirement: 'Report 10 hazards' },
    { id: 'lifesaver', name: 'Lifesaver', icon: '🏆', description: 'Help 50+ lives', tier: 'platinum', requirement: 'Help 50+ lives' },
    { id: 'connector', name: 'Connector', icon: '🤝', description: 'Add 5 contacts', tier: 'bronze', requirement: 'Add 5 contacts' },
    { id: 'communicator', name: 'Communicator', icon: '💬', description: 'Send 20 messages', tier: 'silver', requirement: 'Send 20 messages' },
    { id: 'early_adopter', name: 'Early Adopter', icon: '🚀', description: 'Join during beta', tier: 'gold', requirement: 'Join during beta' },
    { id: 'night_owl', name: 'Night Owl', icon: '🦉', description: 'Report at night', tier: 'bronze', requirement: 'Report between midnight and 5am' },
    { id: 'community_hero', name: 'Community Hero', icon: '🦸', description: 'Help 100+ lives', tier: 'platinum', requirement: 'Help 100+ lives' },
];

const TIER_COLORS: Record<string, string> = {
    bronze: '#CD7F32',
    silver: '#C0C0C0',
    gold: '#FFD700',
    platinum: '#E5E4E2'
};

interface ProfilePageProps {
    profile: Contact;
    isOwnProfile: boolean;
    onLogout: () => void;
    onBack: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ profile, isOwnProfile, onLogout, onBack }) => {
    const [stats, setStats] = useState({ reports: 0, livesHelped: 0, activeDays: 0, contacts: 0 });
    const [achievements, setAchievements] = useState<string[]>([]);
    const [locationSharing, setLocationSharing] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const details = await contactService.getContactDetails(profile.id);
                if (details) {
                    setStats({
                        reports: details.reports_count || 0,
                        livesHelped: details.lives_helped || 0,
                        activeDays: Math.floor((Date.now() - new Date(details.created_at || Date.now()).getTime()) / (1000 * 60 * 60 * 24)) + 1,
                        contacts: 0
                    });
                    setLocationSharing(details.location_sharing ?? true);
                }

                const userAchievements = await contactService.getUserAchievements(profile.id);
                setAchievements(userAchievements || []);

                // For contacts, we might want to fetch *their* network size if public, or just show mutuals?
                // keeping simple for now
                const contacts = await contactService.getContacts(profile.id);
                setStats(prev => ({ ...prev, contacts: contacts.length }));
            } catch (e) {
                console.error("Failed to fetch profile", e);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [profile.id]);

    const toggleLocationSharing = async () => {
        if (!isOwnProfile) return;
        const newValue = !locationSharing;
        setLocationSharing(newValue);
        await contactService.updateProfileSettings(profile.id, { location_sharing: newValue });
    };

    const statusColor = profile.status === SafetyStatus.SAFE ? 'text-green-500'
        : profile.status === SafetyStatus.DANGER ? 'text-red-500'
            : 'text-amber-500';

    const statusBg = profile.status === SafetyStatus.SAFE ? 'bg-green-500'
        : profile.status === SafetyStatus.DANGER ? 'bg-red-500'
            : 'bg-amber-500';

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto animate-fade-in" style={{ backgroundColor: 'var(--bg-deepest)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}>

            {/* NATURE-TECH BACKGROUND EFFECTS */}
            <div className="fixed inset-0 opacity-20 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-transparent to-transparent" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(96,165,250,0.1), transparent)' }} />
                <div className="absolute top-20 -right-20 w-96 h-96 rounded-full blur-[120px] animate-breathe" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)' }} />
                <div className="absolute bottom-40 -left-20 w-64 h-64 rounded-full blur-[100px] animate-leaf-float" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)' }} />
            </div>

            {/* NAV HEADER - Touch Optimized */}
            <div className="fixed top-0 left-0 right-0 p-4 z-[110] flex justify-between items-start bg-gradient-to-b from-black/80 to-transparent">
                <button onClick={onBack} className="p-3 rounded-2xl backdrop-blur-md transition-all active:scale-95" style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <ChevronLeft className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                </button>
                {isOwnProfile && (
                    <button onClick={onLogout} className="p-3 rounded-2xl backdrop-blur-md transition-all active:scale-95" style={{ backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: 'var(--accent-danger)' }}>
                        <LogOut className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* PROFILE HEADER (NATURE-TECH GLASS CARD) */}
            <div className="relative pt-24 px-4 pb-8">
                <div className="backdrop-blur-xl p-6 rounded-3xl relative overflow-hidden shadow-2xl" style={{ backgroundColor: 'var(--bg-glass-strong)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {/* Organic shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-50 animate-shimmer" />

                    <div className="relative z-10 flex flex-col items-center">
                        {/* Avatar Ring */}
                        <div className="relative mb-6 group">
                            <div className="w-32 h-32 rounded-full border-4 p-1 shadow-2xl transition-all duration-500 animate-soft-pulse" style={{ borderColor: statusColor === 'text-green-500' ? 'var(--accent-safe)' : statusColor === 'text-red-500' ? 'var(--accent-danger)' : 'var(--accent-alert)', backgroundColor: 'rgba(0,0,0,0.5)', boxShadow: `0 0 30px currentColor` }}>
                                <img
                                    src={getEffectiveAvatar(profile)}
                                    className="w-full h-full rounded-full bg-black object-cover"
                                    alt="Profile"
                                />
                            </div>
                            {isOwnProfile && (
                                <button className="absolute bottom-0 right-0 p-3 rounded-full text-white transition-all shadow-lg active:scale-95" style={{ backgroundColor: 'var(--accent-primary)', border: '2px solid var(--bg-deepest)' }}>
                                    <Camera className="w-5 h-5" />
                                </button>
                            )}
                            {/* Status Indicator */}
                            <div className="w-6 h-6 rounded-full border-2 animate-soft-pulse shadow-xl absolute top-0 right-0" style={{ borderColor: 'var(--bg-deepest)', backgroundColor: statusBg === 'bg-green-500' ? 'var(--accent-safe)' : statusBg === 'bg-red-500' ? 'var(--accent-danger)' : 'var(--accent-alert)', boxShadow: '0 0 15px currentColor' }} />
                        </div>

                        {/* Name & Role */}
                        <h1 className="text-3xl font-black tracking-wide text-center uppercase" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{profile.name}</h1>

                        {/* NID & Role Row */}
                        <div className="flex items-center gap-2 mt-3">
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl backdrop-blur-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <Shield className="w-3 h-3" style={{ color: statusColor === 'text-green-500' ? 'var(--accent-safe)' : statusColor === 'text-red-500' ? 'var(--accent-danger)' : 'var(--accent-alert)' }} />
                                <span className="text-xs font-mono tracking-widest uppercase" style={{ color: 'var(--text-secondary)' }}>{profile.role || 'OPERATIVE'}</span>
                            </div>

                            {/* Copy ID Button */}
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(profile.id);
                                    const btn = document.getElementById('copy-nid-icon');
                                    if (btn) {
                                        const original = btn.innerHTML;
                                        btn.innerHTML = '✓';
                                        setTimeout(() => btn.innerHTML = original, 2000);
                                    }
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all group active:scale-95"
                                style={{ backgroundColor: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)' }}
                                title="Click to Copy NID"
                            >
                                <span className="text-xs font-mono tracking-wider" style={{ color: 'var(--accent-primary)' }}>ID: {profile.id.substring(0, 8)}...</span>
                                <span id="copy-nid-icon" className="text-xs" style={{ color: 'var(--accent-primary)' }}>❐</span>
                            </button>
                        </div>

                        {/* Quick Actions (Non-Owner) */}
                        {!isOwnProfile && (
                            <div className="flex gap-4 mt-6 w-full max-w-xs">
                                <button className="flex-1 py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg" style={{ backgroundColor: 'var(--accent-primary)', color: 'white', boxShadow: '0 0 20px rgba(96,165,250,0.3)' }}>
                                    <MessageSquare className="w-4 h-4" /> Message
                                </button>
                                <button className="flex-1 py-3.5 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all active:scale-95" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}>
                                    <Phone className="w-4 h-4" /> Call
                                </button>
                            </div>
                        )}

                        {/* Location (If Shared) */}
                        {profile.locationName && (
                            <div className="mt-4 flex items-center gap-2 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                                <MapPin className="w-3 h-3" />
                                {profile.locationName}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* IMPACT STATS - Nature-Tech */}
            <div className="px-4 mb-6">
                <div className="grid grid-cols-2 gap-3">
                    <div className="backdrop-blur rounded-2xl p-5 border transition-all group" style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex justify-between items-start mb-2">
                            <div className="p-2 rounded-xl" style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: 'var(--accent-primary)' }}>
                                <Target className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>REPORTS</span>
                        </div>
                        <div className="text-2xl font-black transition-colors" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{stats.reports}</div>
                    </div>
                    <div className="backdrop-blur rounded-2xl p-5 border transition-all group" style={{ backgroundColor: 'var(--bg-glass)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex justify-between items-start mb-2">
                            <div className="p-2 rounded-xl" style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: 'var(--accent-danger)' }}>
                                <Heart className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>LIVES</span>
                        </div>
                        <div className="text-2xl font-black transition-colors" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{stats.livesHelped}</div>
                    </div>
                </div>
            </div>

            {/* ACHIEVEMENTS */}
            <div className="px-4 mb-24">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        Service Record
                    </h3>
                    <span className="text-xs font-mono text-gray-600">
                        {achievements.length} / {ALL_BADGES.length}
                    </span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {ALL_BADGES.map((badge) => {
                        const isEarned = achievements.includes(badge.id);
                        if (!isEarned && !isOwnProfile) return null; // Hide unearned on others' profiles

                        return (
                            <motion.div
                                key={badge.id}
                                className={`relative p-4 rounded-xl flex items-center gap-4 border overflow-hidden ${isEarned
                                    ? 'bg-gradient-to-r from-white/5 to-transparent border-white/10'
                                    : 'bg-black/20 border-white/5 opacity-50 grayscale'
                                    }`}
                            >
                                {isEarned && <div className="absolute inset-0 bg-[var(--neon-cyan)]/5" />}

                                <div className="text-3xl relative z-10">{badge.icon}</div>
                                <div className="flex-1 relative z-10">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-bold text-white text-sm">{badge.name}</h4>
                                        {isEarned && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--neon-cyan)]/20 text-[var(--neon-cyan)] border border-[var(--neon-cyan)]/30 font-mono">EARNED</span>}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{badge.description}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* SETTINGS (OWNER ONLY) */}
            {isOwnProfile && (
                <div className="px-4 pb-32">
                    <div className="border-t border-white/10 pt-6">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 px-2">Configuration</h3>

                        <button
                            onClick={toggleLocationSharing}
                            className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors mb-3"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${locationSharing ? 'bg-green-500/10 text-green-500' : 'bg-gray-700/50 text-gray-400'}`}>
                                    {locationSharing ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                </div>
                                <div className="text-left">
                                    <p className="text-sm font-bold text-white">Location Broadcast</p>
                                    <p className="text-[10px] text-gray-500 font-mono">{locationSharing ? 'VISIBLE TO SQUAD' : 'STEALTH MODE ACTIVE'}</p>
                                </div>
                            </div>
                            <div className={`w-10 h-6 rounded-full p-1 transition-colors ${locationSharing ? 'bg-green-500' : 'bg-gray-700'}`}>
                                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${locationSharing ? 'translate-x-4' : 'translate-x-0'}`} />
                            </div>
                        </button>

                        <button className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                                    <Edit2 className="w-5 h-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-sm font-bold text-white">Edit Profile</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ProfilePage;
